import { z } from 'zod';

/**
 * Unified card schema for all Nova surfaces: assistant chat, record insights,
 * summary brief, and meeting.
 *
 * The AGENT owns section SELECTION + ORDER + CONTENT. The APP owns all visual
 * layout per section type, responsively — no columns/cells in the wire format;
 * horizontal grouping lives inside section components and collapses on mobile.
 *
 * Foundry strict structured outputs declare every property in `required[]` with
 * `additionalProperties:false`. In practice the model still omits fields inside the
 * sections `anyOf` (strict doesn't enforce union branches) and emits bare numbers
 * for display strings — so each optional defaults a missing key to null, and value
 * fields coerce, rather than dropping a whole section over one stray field. Each
 * agent's `structured_outputs.card_response` carries `strict:true` and matches this
 * schema's shape.
 */

// ── Shared scalars ──────────────────────────────────────────────────────────

/** Severity/tone accent for callouts. Maps to design-token color sets. */
const Accent = z.enum(['info', 'success', 'warning', 'risk']);

/** Direction hint for a metric/chart delta; the agent decides what's "good". */
const TrendDir = z.enum(['up', 'down', 'flat']).nullable().default(null);

/**
 * A number the agent may have emitted as a string — "80", "80%", "7,816".
 * The model is not reliably typed here, and a bare `z.number()` rejects a
 * stringified value, which drops the whole section. Strip non-numeric chars
 * and parse; an unparseable value is left as-is so it still fails loudly.
 */
const LooseNumber = z.preprocess(v => {
  if (typeof v === 'string') {
    const n = parseFloat(v.replace(/[^0-9.\-]/g, ''));
    return Number.isNaN(n) ? v : n;
  }
  return v;
}, z.number());

// ── Card-level actions ──────────────────────────────────────────────────────

/**
 * A card-level chip/button OR an action attached to an insight section.
 *  - kind 'prompt': `value` is sent as the next user turn (tap-to-ask chip).
 *  - kind 'tool':   `value` is the name of a client @AITool to invoke
 *                   (registry in src/app/ai/tools/), optionally followed by
 *                   `:` and the value of its first required parameter, for
 *                   example `view_contact:<contact id>`.
 */
const Action = z.object({
  label: z.string(),
  kind: z.enum(['prompt', 'tool']),
  value: z.string(),
});

// ── Section item shapes ─────────────────────────────────────────────────────

const MetricLike = {
  label: z.string(),
  // The model may emit a bare number (336) for this display field; coerce to
  // string so the section survives validation instead of being dropped.
  value: z.coerce.string(),
  delta: z.string().nullable().default(null),
  trend: TrendDir,
};

const ChartItem = z.object({
  label: z.string(),
  value: LooseNumber,
  /**
   * Optional 2nd series value. For `grouped` charts it's the second bar
   * (e.g. NRx vs TRx); for `bullet` charts it's the TARGET the `value` is
   * measured against (e.g. value 7816 vs value2 9000 quota).
   */
  value2: LooseNumber.nullable().default(null),
  /** Pre-formatted end-of-row value, e.g. "7,816 TRx". Falls back to value+unit. */
  display: z.string().nullable().default(null),
});

const ProgressStep = z.object({
  label: z.string(),
  current: z.boolean(),
});

/** One labelled linear progress bar (the `bars` progress variant). */
const ProgressBar = z.object({
  label: z.string(),
  percent: LooseNumber,
  /** Right-side label, e.g. "8 of 10" or "80%". The agent often sends `display`. */
  detail: z.string().nullable().default(null),
  display: z.string().nullable().default(null),
});

const FactItem = z.object({
  label: z.string(),
  // Record-field values are often numeric; coerce so a numeric value isn't dropped.
  value: z.coerce.string(),
});

const EntityItem = z.object({
  kind: z.enum(['contact', 'account']),
  id: z.string(),
  label: z.string(),
  sublabel: z.string().nullable().default(null),
});

/** One dated node on a `timeline` section's time axis. */
const TimelineEvent = z.object({
  label: z.string(),
  /** Pre-formatted date or relative time, e.g. "Jun 12" or "2 days ago". */
  when: z.string(),
  detail: z.string().nullable().default(null),
  /** Node-dot tone; reuses the callout accent token set. */
  accent: Accent.nullable().default(null),
});

// ── Sections (discriminated union on `type`) ────────────────────────────────

export const SectionSchema = z.discriminatedUnion('type', [
  // markdown prose (brief body, assistant prose)
  z.object({
    type: z.literal('text'),
    text: z.string(),
  }),

  // pull-quote banner
  z.object({
    type: z.literal('callout'),
    text: z.string(),
    accent: Accent.nullable().default(null),
  }),

  // severity-tagged insight card
  z.object({
    type: z.literal('insight'),
    severity: z.enum(['urgent', 'warning', 'recommended']),
    headline: z.string(),
    observation: z.string(),
    action: z.string().nullable().default(null),
    /** CRM tool NAMES the rep should use, source order (display-only labels). */
    tools: z.array(z.string()).nullable().default(null),
    /** Executable actions for this insight (prompt chips / @AITool buttons). */
    actions: z.array(Action).nullable().default(null),
  }),

  // one headline metric (one section per metric)
  z.object({
    type: z.literal('metric'),
    ...MetricLike,
  }),

  // a compact strip of KPIs — several metrics in one responsive row
  z.object({
    type: z.literal('stats'),
    title: z.string().nullable().default(null),
    items: z.array(z.object(MetricLike)),
  }),

  // bars (ranked) | trend (sparkline) | grouped (2 series) | diverging (± movers) | donut (share) | bullet (actual vs target)
  z.object({
    type: z.literal('chart'),
    variant: z.enum(['bars', 'trend', 'grouped', 'diverging', 'donut', 'bullet']),
    title: z.string().nullable().default(null),
    unit: z.string().nullable().default(null),
    /** Legend for the two series of a `grouped` chart, e.g. ['TRx', 'NRx']. */
    seriesLabels: z.array(z.string()).nullable().default(null),
    items: z.array(ChartItem),
  }),

  // ring (radial) | ladder (stepped) | bars (labelled linear bars)
  z.object({
    type: z.literal('progress'),
    variant: z.enum(['ring', 'ladder', 'bars']),
    title: z.string().nullable().default(null),
    label: z.string().nullable().default(null),
    percent: LooseNumber.nullable().default(null),
    detail: z.string().nullable().default(null),
    steps: z.array(ProgressStep).nullable().default(null),
    bars: z.array(ProgressBar).nullable().default(null),
  }),

  // bulleted list
  z.object({
    type: z.literal('list'),
    title: z.string().nullable().default(null),
    items: z.array(z.string()),
  }),

  // label/value fact rows
  z.object({
    type: z.literal('facts'),
    title: z.string().nullable().default(null),
    items: z.array(FactItem),
  }),

  // tappable contact/account mini-cards
  z.object({
    type: z.literal('entities'),
    title: z.string().nullable().default(null),
    items: z.array(EntityItem),
  }),

  // dated event nodes on a vertical time axis (recent activity / what's next)
  z.object({
    type: z.literal('timeline'),
    title: z.string().nullable().default(null),
    items: z.array(TimelineEvent),
  }),
]);

// ── Card root ───────────────────────────────────────────────────────────────

export const CardResponseSchema = z.object({
  title: z.string().nullable().default(null),
  /** ORDERED. The agent chooses which sections appear and in what order. */
  sections: z.array(SectionSchema),
  /** Card-level chips/buttons. */
  actions: z.array(Action).nullable().default(null),
});

export type CardResponse = z.infer<typeof CardResponseSchema>;
export type Section = z.infer<typeof SectionSchema>;
export type CardAction = z.infer<typeof Action>;
export type Accent = z.infer<typeof Accent>;

// ── Constants ────────────────────────────────────────────────────────────────

// ── Safe parse helper ─────────────────────────────────────────────────────────

/**
 * Coerce arbitrary agent output into a renderable `CardResponse`. NEVER throws:
 * on malformed or partial input — bad JSON, a missing `sections` array, a section
 * that fails its branch — it falls back to a single `text` section so the surface
 * degrades gracefully instead of showing a broken bubble. A weaker model
 * occasionally emitting an off-schema branch must not take the whole card down.
 *
 * Accepts either an already-parsed object or a raw JSON string.
 */
export function parseCardResponse(input: unknown): CardResponse {
  const raw = typeof input === 'string' ? safeJson(input) : input;
  // Map the agent's common progress field aliases (`value`→`percent`,
  // `display`→`detail`) onto the schema names before validating, so a section
  // that only differs by field name renders instead of being dropped.
  const value = normalizeCard(raw);

  // Surface anything the renderer can't fully honor — a section the schema
  // rejects (dropped) OR a section that parses but carried fields the schema
  // stripped (a field-name mismatch whose data silently won't render). Logged
  // before parsing so the diagnostic fires even on the otherwise-happy path.
  if (isRecord(value) && Array.isArray(value['sections'])) {
    diagnoseSections(value['sections']);
  }

  const full = CardResponseSchema.safeParse(value);
  if (full.success) {
    return full.data;
  }

  // Partial recovery: keep the well-formed sections, drop the malformed ones,
  // and preserve title/actions when they individually validate.
  if (isRecord(value)) {
    const sections = recoverSections(value['sections']);
    if (sections.length > 0) {
      return {
        title: recoverNullableString(value['title']),
        sections,
        actions: recoverActions(value['actions']),
      };
    }
  }

  return fallbackCard(value);
}

/**
 * A card with one `text` section of markdown prose and, optionally, card-level actions. Used for
 * cards the app composes itself, such as the reply to a failed turn with its Retry action.
 */
export function textCard(text: string, actions: CardAction[] | null = null): CardResponse {
  return { title: null, sections: [{ type: 'text', text }], actions };
}

// ── Recovery internals ─────────────────────────────────────────────────────────

function safeJson(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    // Not JSON — render the raw string as prose rather than losing it.
    return raw;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Normalize known agent field-name drift before validation. Today this only
 * touches `progress` sections, where the model sometimes emits the percent as
 * `value` and the right-side label as `display` instead of `percent`/`detail`.
 * Canonical fields always win; aliases fill in only when the canonical key is
 * absent, then are removed so diagnostics don't flag them. Returns the input
 * untouched when there are no sections to normalize.
 */
function normalizeCard(value: unknown): unknown {
  if (!isRecord(value) || !Array.isArray(value['sections'])) {
    return value;
  }
  return { ...value, sections: value['sections'].map(normalizeProgressSection) };
}

function normalizeProgressSection(section: unknown): unknown {
  if (!isRecord(section) || section['type'] !== 'progress') {
    return section;
  }
  const s: Record<string, unknown> = { ...section };
  if (s['percent'] == null && s['value'] != null) s['percent'] = s['value'];
  delete s['value'];
  if (s['detail'] == null && typeof s['display'] === 'string') s['detail'] = s['display'];
  delete s['display'];
  if (Array.isArray(s['bars'])) {
    s['bars'] = s['bars'].map(bar => {
      if (!isRecord(bar)) return bar;
      const b: Record<string, unknown> = { ...bar };
      if (b['percent'] == null && b['value'] != null) b['percent'] = b['value'];
      delete b['value'];
      // `display` stays — it's a valid ProgressBar field.
      return b;
    });
  }
  return s;
}

/** The `type` of a raw section candidate, for diagnostics. */
function sectionType(candidate: unknown): string {
  return isRecord(candidate) && typeof candidate['type'] === 'string'
    ? candidate['type']
    : '(unknown)';
}

/**
 * Dev/runtime diagnostics for card rendering. Walks the raw sections and logs:
 *  - an ERROR for any section the schema rejects (it will be dropped), naming
 *    the failing fields, and
 *  - a WARN for any section that validates but carried keys the schema stripped
 *    (almost always a field-name mismatch — e.g. progress sending `value`
 *    instead of `percent` — whose data silently won't render).
 * Never throws; purely observational so a logging slip can't break a card.
 */
function diagnoseSections(raw: unknown[]): void {
  raw.forEach((candidate, index) => {
    const parsed = SectionSchema.safeParse(candidate);
    if (!parsed.success) {
      console.error(
        `[NovaCard] section #${index} (type="${sectionType(candidate)}") DROPPED — failed schema validation:`,
        parsed.error.issues.map(i => `${i.path.join('.') || '(root)'}: ${i.message}`),
        candidate,
      );
      return;
    }
    if (isRecord(candidate)) {
      const kept = parsed.data as Record<string, unknown>;
      const stripped = Object.keys(candidate).filter(k => !(k in kept));
      if (stripped.length > 0) {
        console.warn(
          `[NovaCard] section #${index} (type="${sectionType(candidate)}") ignored unknown field(s): ${stripped.join(', ')} — likely a field-name mismatch; that data will not render.`,
        );
      }
    }
  });
}

function recoverSections(raw: unknown): Section[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const kept: Section[] = [];
  for (const candidate of raw) {
    const parsed = SectionSchema.safeParse(candidate);
    if (parsed.success) {
      kept.push(parsed.data);
    }
  }
  return kept;
}

function recoverActions(raw: unknown): CardAction[] | null {
  if (!Array.isArray(raw)) {
    return null;
  }
  const kept: CardAction[] = [];
  for (const candidate of raw) {
    const parsed = Action.safeParse(candidate);
    if (parsed.success) {
      kept.push(parsed.data);
    }
  }
  return kept.length > 0 ? kept : null;
}

function recoverNullableString(raw: unknown): string | null {
  return typeof raw === 'string' ? raw : null;
}

/**
 * Last resort: surface whatever prose we can salvage as a single text section.
 * A bare string becomes its own text; anything else yields an empty-text card so
 * the renderer shows the shell (and its disclaimer) without crashing.
 */
function fallbackCard(value: unknown): CardResponse {
  console.error(
    '[NovaCard] no renderable sections — falling back to a plain text card. Raw value:',
    value,
  );
  return textCard(typeof value === 'string' ? value : '');
}
